import { PrintJob, PrintJobStatus } from "@/types.d";
import type { OrderItemsHydrateOptions } from "@/utils/orderItemsIncludes";
import { http } from "@/utils/http-common";

export function getActiveJobs(mode: "list" | "full" | "core" = "list", hydrate?: Partial<OrderItemsHydrateOptions>, printerId?: number | null) {
  const params = new URLSearchParams();
  if (mode === "list" || mode === "core") params.append("mode", mode);
  if (printerId === null) params.append("printerId", "null");
  else if (printerId != null) params.append("printerId", String(printerId));
  if (hydrate?.includes?.length) params.append("includes", hydrate.includes.join(","));
  if (hydrate?.orderCustomFieldIds?.length) params.append("orderCustomFieldIds", hydrate.orderCustomFieldIds.join(","));
  if (hydrate?.orderItemCustomFieldIds?.length) params.append("orderItemCustomFieldIds", hydrate.orderItemCustomFieldIds.join(","));
  const qs = params.toString();
  return http.get(`/printJobs/notFinished${qs ? `?${qs}` : ""}`).then((res) => {
    return res.data.data as PrintJob[];
  });
}

export function getAllJobs() {
  return http
    .get(
      `/printJobs?with=file.preview,file.otherPreviews,orderItem.currentFile.preview,orderItem.currentFile.otherPreviews,orderItem.order,orderItem.material,orderItem.order.customer,printer,scheme,layout`,
    )
    .then((res) => {
      return res.data.data;
    });
}

export function getFinishedPrintJobs(limit?: number, page?: number, sortField?: string, sortOrder?: 0 | 1 | -1 | null, filters?: string) {
  const params = new URLSearchParams();
  if (limit) params.append("limit", limit.toString());
  if (page) params.append("page", page.toString());
  const orderDirection = sortOrder && sortOrder >= 0 ? "+" : "-";
  const order = sortField ? `${orderDirection}${sortField}` : undefined;
  if (order) params.append("order", order);
  // params.append("with", "orderItem.currentFile.preview,orderItem.order,orderItem.material,orderItem.order.customer,orderItem.order.customFieldValues,printer,scheme,layout");
  return http.get(`/printJobs/finished?${filters}`, { params }).then((res) => {
    return res.data;
  });
}

export function getPrintJobsHistoryByOrderItem(limit?: number, page?: number, sortField?: string, sortOrder?: 0 | 1 | -1 | null, filters?: string) {
  const params = new URLSearchParams();
  if (limit) params.append("limit", limit.toString());
  if (page) params.append("page", page.toString());
  const orderDirection = sortOrder && sortOrder >= 0 ? "+" : "-";
  const order = sortField ? `${orderDirection}${sortField}` : undefined;
  if (order) params.append("order", order);
  return http.get(`/printJobs/historyByOrderItem?${filters}`, { params }).then((res) => {
    return res.data;
  });
}

export function assignJobToPrinter(jobId: number, printerId: number | null) {
  const status = printerId ? PrintJobStatus.ASSIGNED : PrintJobStatus.PENDING;
  return http.put(`/printJobs/${jobId}`, { printerId, status }).then((res) => {
    return res.data;
  });
}

export function editPrintJob(jobId: number, data: Partial<PrintJob>) {
  return http.put(`/printJobs/${jobId}`, data).then((res) => {
    return res.data;
  });
}

export function bulkEditPrintJobs(jobs: Array<{ id: number } & Partial<PrintJob>>) {
  return http.put(`/printJobs/bulk`, jobs).then((res) => {
    return res.data;
  });
}

export function createPrintJob(data: Partial<PrintJob>) {
  return http.post(`/printJobs`, data).then((res) => {
    return res.data;
  });
}

export function emitPrintJobDelete(printJobIds: number[]) {
  return http.post(`/printJobs/emit-delete`, { printJobIds }).then((res) => {
    return res.data;
  });
}

export function emitPrintJobMove(printJobId: number, moveType: "moveUp" | "moveDown" | "moveToTop" | "moveToBottom") {
  return http.post(`/printJobs/emit-move`, { printJobId, moveType }).then((res) => {
    return res.data;
  });
}

export async function exportPrintJobsExcel(payload: {
  ids: number[];
  columns: { field: string; header: string; type?: string; customFieldId?: number; entity?: string; isJobColumn?: boolean }[];
  filename?: string;
}) {
  const { filename, ...body } = payload;
  const response = await http.post("/printJobs/export/excel", body, { responseType: "blob" });
  const url = window.URL.createObjectURL(
    new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename || "print-jobs.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
